import { Component } from 'react';
import dayjs from 'dayjs';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, Label } from 'recharts';

import http from 'services/http';
import formatMoney from 'utils/formatMoney';
import { temModulo2Pra500 } from 'utils/modulos';

import styles from './styles.module.css';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658'];

export default class Dashboard extends Component {
    constructor(props) {
        super(props);

        const hoje = dayjs().format('YYYY-MM-DD');

        this.state = {
            startDate: hoje,
            endDate: hoje,
            loading: false,
            dashboard: null,
            gerentes: [],
            cambistas: [],
            supervisores: [],
            loterias: [],
            gerenteId: '',
            cambistaId: '',
            supervisorId: '',
            loteriaId: '',
            categoria: ''
        };
    }

    componentDidMount() {
        this.buscarGerentes();
        this.buscarCambistas();
        this.buscarSupervisores();
        this.buscarLoterias();
        this.modulos = JSON.parse(localStorage.getItem('usuario'))?.modulosAtivos ?? [];
    }

    buscarGerentes = async () => {
        try {
            const response = await http.get('/usuarios', {
                params: { nivel: 'gerente', resumida: true }
            });
            this.setState({ gerentes: response.data.data });
        } catch (e) {}
    };

    // Sem escopo traz todos os cambistas; com gerente ou grupo, só os de dentro dele.
    buscarCambistas = async ({ gerenteId, supervisorId } = {}) => {
        try {
            const response = await http.get('/usuarios', {
                params: {
                    nivel: 'operador',
                    resumida: true,
                    gerente_id: gerenteId || undefined,
                    supervisor_id: supervisorId || undefined
                }
            });
            this.setState({ cambistas: response.data.data });
        } catch (e) {}
    };

    buscarSupervisores = async () => {
        try {
            const response = await http.get('/supervisores', { params: { resumida: true } });
            this.setState({ supervisores: response.data.data ?? [] });
        } catch (e) {}
    };

    buscarLoterias = async () => {
        try {
            const response = await http.get('/loterias', {params: {todas: 1}});
            this.setState({ loterias: response.data.data });
        } catch (e) {}
    };


    // Grupo e gerente são excludentes: a API aplica os dois com AND e a combinação
    // cruzada devolveria dashboard vazio. Trocar qualquer um dos dois zera o cambista
    // e recarrega a lista no escopo do novo filtro.
    mudarGerente = (id) => {
        const gerenteId = id || '';
        this.setState({ gerenteId, cambistaId: '', supervisorId: '' });
        this.buscarCambistas({ gerenteId });
    };

    mudarSupervisor = (id) => {
        const supervisorId = id || '';
        this.setState({ supervisorId, gerenteId: '', cambistaId: '' });
        this.buscarCambistas({ supervisorId });
    };

    buscarDashboard = async (e) => {
        e.preventDefault();

        if (!this.state.startDate || !this.state.endDate) {
            return;
        }

        this.setState({ loading: true });

        try {
            const params = {
                data_inicial: dayjs(this.state.startDate).format('YYYY-MM-DD'),
                data_final: dayjs(this.state.endDate).format('YYYY-MM-DD')
            };

            if (this.state.cambistaId) {
                params.cambista_id = this.state.cambistaId;
            } else if (this.state.gerenteId) {
                params.gerente_id = this.state.gerenteId;
            }

            if (this.state.supervisorId) params.supervisor_id = this.state.supervisorId;

            if (this.state.loteriaId) params.loteria_id = this.state.loteriaId;
            if (this.state.categoria) params.categoria = this.state.categoria;

            const response = await http.get('/dashboard', { params });

            this.setState({ dashboard: response.data.data });
        } catch (error) {
            console.error('Erro ao buscar dashboard:', error);
        }

        this.setState({ loading: false });
    };

    renderInformacaoGeral() {
        const { dashboard } = this.state;
        if (!dashboard?.informacao_geral) return null;

        const info = dashboard.informacao_geral;

        return (
            <div className={styles.cardSection}>
                <h3>Informações Gerais</h3>
                <div className={styles.cardsGrid}>
                    <div className={styles.card}>
                        <span className={styles.cardLabel}>Última Aposta</span>
                        <span className={styles.cardValue}>
                            {info.ultima_aposta ? dayjs(info.ultima_aposta).format('DD/MM - HH:mm') : '-'}
                        </span>
                    </div>
                    <div className={styles.card}>
                        <span className={styles.cardLabel}>Qtd. Apostas</span>
                        <span className={styles.cardValue}>{info.quantidade_apostas}</span>
                    </div>
                    <div className={styles.card}>
                        <span className={styles.cardLabel}>Qtd. em Aberto</span>
                        <span className={styles.cardValue}>{info.quantidade_aberta}</span>
                    </div>
                    <div className={styles.card}>
                        <span className={styles.cardLabel}>Vendedores Ativos</span>
                        <span className={styles.cardValue}>{info.quantidade_vendedores}</span>
                    </div>
                    <div className={styles.card}>
                        <span className={styles.cardLabel}>% Prêmio</span>
                        <span className={styles.cardValue}>{info.porcentagem_premio}%</span>
                    </div>
                </div>

                <div className={styles.resumoGrid}>
                    <div className={`${styles.resumoCard} ${styles.entrada}`}>
                        <span className={styles.resumoLabel}>Entrada</span>
                        <span className={styles.resumoValor}>{formatMoney(info.entrada)}</span>
                    </div>
                    <div className={`${styles.resumoCard} ${styles.premios}`}>
                        <span className={styles.resumoLabel}>Prêmios</span>
                        <span className={styles.resumoValor}>{formatMoney(info.premios)}</span>
                    </div>
                    <div className={`${styles.resumoCard} ${styles.comissao}`}>
                        <span className={styles.resumoLabel}>% Vendedores</span>
                        <span className={styles.resumoValor}>{formatMoney(info.comissao_vendedores)}</span>
                    </div>
                    <div className={`${styles.resumoCard} ${styles.comissao}`}>
                        <span className={styles.resumoLabel}>% Gerentes</span>
                        <span className={styles.resumoValor}>{formatMoney(info.comissao_gerentes)}</span>
                    </div>
                    <div className={`${styles.resumoCard} ${styles.total} ${info.total >= 0 ? styles.positivo : styles.negativo}`}>
                        <span className={styles.resumoLabel}>Total</span>
                        <span className={styles.resumoValor}>{formatMoney(info.total)}</span>
                    </div>
                </div>
            </div>
        );
    }

    renderLabelPercentual = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
        if (percent < 0.04) return null;
        const RADIAN = Math.PI / 180;
        const radius = innerRadius + (outerRadius - innerRadius) * 0.6;
        const x = cx + radius * Math.cos(-midAngle * RADIAN);
        const y = cy + radius * Math.sin(-midAngle * RADIAN);
        return (
            <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central"
                style={{ fontSize: 11, fontWeight: 700 }}>
                {`${(percent * 100).toFixed(0)}%`}
            </text>
        );
    };

    renderGraficoPizza(titulo, dados, nameKey = 'modalidade') {
        if (!dados || dados.length === 0) return null;

        return (
            <div className={styles.chartCard}>
                <h4>{titulo}</h4>
                <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                        <Pie
                            data={dados}
                            dataKey="valor"
                            nameKey={nameKey}
                            cx="50%"
                            cy="45%"
                            outerRadius={75}
                            innerRadius={35}
                            labelLine={false}
                            label={this.renderLabelPercentual}
                        >
                            {dados.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatMoney(value)} />
                        <Legend iconType="circle" iconSize={10} />
                    </PieChart>
                </ResponsiveContainer>
            </div>
        );
    }

    renderGraficoExtracoes() {
        const { dashboard } = this.state;
        if (!dashboard?.extracoes_mais_jogadas) return null;

        const dados = dashboard.extracoes_mais_jogadas.map(item => ({
            modalidade: item.nome,
            valor: item.valor
        }));

        return this.renderGraficoPizza('Extracoes Mais Jogadas', dados);
    }

    renderTopNumeros(titulo, dados) {
        if (!dados || dados.length === 0) return null;

        return (
            <div className={styles.topCard}>
                <h4>{titulo}</h4>
                <table className={`table table-sm table-striped ${styles.tabelaNumeros}`}>
                    <thead>
                        <tr>
                            <th>Posição</th>
                            <th>Quantidade</th>
                            <th>Número</th>
                        </tr>
                    </thead>
                    <tbody>
                        {dados.map((item, index) => (
                            <tr key={index}>
                                <td>{index + 1}º</td>
                                <td>{item.quantidade}x</td>
                                <td><strong>{item.numero}</strong></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    renderTopCambistas(titulo, dados) {
        if (!dados || dados.length === 0) return null;

        return (
            <div className={styles.topCard}>
                <h4>{titulo}</h4>
                <table className="table table-sm">
                    <thead>
                        <tr>
                            <th>Cambista</th>
                            <th>Venda</th>
                            <th>Comissao</th>
                            <th>Premio</th>
                            <th>Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {dados.map((item, index) => (
                            <tr key={index}>
                                <td>{item.nome}</td>
                                <td>{formatMoney(item.venda)}</td>
                                <td>{formatMoney(item.comissao)}</td>
                                <td>{formatMoney(item.premio)}</td>
                                <td style={{ color: item.total >= 0 ? '#5cb85c' : '#d9534f' }}>
                                    <strong>{formatMoney(item.total)}</strong>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    render() {
        const { dashboard, loading, startDate, endDate, gerentes, cambistas, gerenteId, cambistaId } = this.state;

        return (
            <>
                <div className="titulo-principal">Dashboard</div>
                <div className="conteudo-principal">
                    <form onSubmit={this.buscarDashboard}>
                        <div className="d-flex quatro-colunas justify-content-start">
                        <div className="form-group">
                            <label>Grupo Gerentes</label>
                            <select
                                onChange={(e) => this.mudarSupervisor(e.target.value)}
                                className="form-control"
                                value={this.state.supervisorId}
                            >
                                <option value="">Todos</option>
                                {this.state.supervisores.map((s) => (
                                    <option key={s.id} value={s.id}>
                                        {s.nome}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="form-group">
                            <label>Gerente</label>
                            <select
                                onChange={(e) => this.mudarGerente(e.target.value)}
                                className="form-control"
                                value={gerenteId}
                            >
                                <option value="">Todos</option>
                                {gerentes.map((g) => (
                                    <option key={g.id} value={g.id}>
                                        {g.login.toUpperCase()}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="form-group">
                            <label>Cambista</label>
                            <select
                                onChange={(e) => this.setState({ cambistaId: e.target.value })}
                                className="form-control"
                                value={cambistaId}
                            >
                                <option value="">Todos</option>
                                {cambistas.map((c) => (
                                    <option key={c.id} value={c.id}>
                                        {c.login.toUpperCase()}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="form-group">
                            <label>Categoria</label>
                            <select
                                onChange={(e) => this.setState({ categoria: e.target.value, loteriaId: '' })}
                                className="form-control"
                                value={this.state.categoria}
                            >
                                <option value="">Todos</option>
                                {this.modulos?.includes('JB') && <option value="jb">Tradicional</option>}
                                {this.modulos?.includes('Bolão') && <option value="bolao">Bolão</option>}
                                {this.modulos?.includes('Bolão de Dezena') && <option value="bolao_dezena">Bolão Dezena</option>}
                                {this.modulos?.includes('Seninha') && <option value="seninha">Seninha</option>}
                                {this.modulos?.includes('Sena Brasil') && <option value="sena_brasil">Sena Brasil</option>}
                                {temModulo2Pra500(this.modulos) && <option value="dois_quinhentos">2 Pra 500</option>}
                                {this.modulos?.includes('Rifa') && <option value="rifa">Rifa</option>}
                                {this.modulos?.includes('Raspadinha') && <option value="raspadinha">Raspadinha</option>}
                            </select>
                        </div>
                        </div>

                        <div className="d-flex quatro-colunas justify-content-start">
                        {(!this.state.categoria || this.state.categoria === 'jb') && (
                            <div className="form-group">
                                <label>Loteria</label>
                                <select
                                    onChange={(e) => this.setState({ loteriaId: e.target.value })}
                                    className="form-control"
                                    value={this.state.loteriaId}
                                >
                                    <option value="">Todos</option>
                                    {this.state.loterias.map((l) => (
                                        <option key={l.id} value={l.id}>{l.nome}</option>
                                    ))}
                                </select>
                            </div>
                        )}
                        <div className="form-group">
                            <label>Data inicial</label>
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => this.setState({ startDate: e.target.value })}
                                className="form-control"
                            />
                        </div>
                        <div className="form-group">
                            <label>Data final</label>
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => this.setState({ endDate: e.target.value })}
                                className="form-control"
                            />
                        </div>
                        <div className="form-group">
                            <label style={{ display: 'block', visibility: 'hidden' }}>.</label>
                            <button type="submit" className="btn btn-primary w-100" style={{ height: '38px' }}>
                                Buscar
                            </button>
                        </div>
                        </div>
                    </form>
                </div>

                {loading ? (
                    <>
                    <p>Carregando dados do dashboard, Isso pode levar alguns segundos, espere na página...</p>
                    <div className="loader" />
                    </>
                ) : dashboard && (
                    <div className={styles.dashboardContent}>
                        {this.renderInformacaoGeral()}

                        <div className={styles.chartsRow}>
                            {this.renderGraficoPizza('Categorias Mais Jogadas', dashboard.categorias_mais_jogadas)}
                            {this.renderGraficoPizza('Modalidades Mais Jogadas', dashboard.modalidades_mais_jogadas)}
                            {this.renderGraficoExtracoes()}
                        </div>

                        <div className={styles.topNumerosGrid}>
                            {this.renderTopNumeros('Top 5 Milhares', dashboard.top_milhares)}
                            {this.renderTopNumeros('Top 5 Centenas', dashboard.top_centenas)}
                            {this.renderTopNumeros('Top 5 Dezenas', dashboard.top_dezenas)}
                            {this.renderTopNumeros('Top 5 Grupos', dashboard.top_grupos)}
                        </div>

                        <div className={styles.topCambistasGrid}>
                            {this.renderTopCambistas('Top 5 Cambistas - Mais Jogos', dashboard.cambistas_top_jogos)}
                            {this.renderTopCambistas('Top 5 Cambistas - Maior Lucro', dashboard.cambistas_top_lucro)}
                        </div>
                    </div>
                )}
            </>
        );
    }
}
